/**
 * Session 60.27 · Sign in with Apple button.
 *
 * Renders Apple's official black pill treatment. Handles both native
 * (Capacitor plugin) and web (dynamic Apple JS SDK) paths — the backend
 * contract is the same: identity_token + server-issued nonce POSTed to
 * /api/auth/apple.
 *
 * Placement: Login + Signup, above the existing Google button, per
 * Apple guideline 4.8.
 */
import { useState } from "react";
import api from "../lib/api";

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.44-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

// Fetch a fresh, single-use nonce from the backend.
async function getServerNonce() {
  const r = await api.post("/auth/apple/nonce");
  return r.data.nonce;
}

async function isNativeIOS() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.getPlatform() === "ios";
  } catch { return false; }
}

async function runNativeAppleSignIn(nonce) {
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  const result = await SignInWithApple.authorize({
    clientId: "com.workmetier.app",
    redirectURI: `${window.location.origin}/login/apple/callback`,
    scopes: "email name",
    nonce,
  });
  const r = result?.response || {};
  return {
    identity_token: r.identityToken,
    authorization_code: r.authorizationCode || null,
    first_name: r.givenName || null,
    last_name: r.familyName || null,
  };
}

async function runWebAppleSignIn(nonce) {
  // Lazy-load Apple's Sign in with Apple JS SDK.
  await new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const s = document.createElement("script");
    s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Apple JS SDK"));
    document.head.appendChild(s);
  });
  window.AppleID.auth.init({
    clientId: "com.workmetier.web",
    scope: "name email",
    redirectURI: `${window.location.origin}/login/apple/callback`,
    usePopup: true,
    nonce,
  });
  const data = await window.AppleID.auth.signIn();
  const authn = data?.authorization || {};
  const nameUser = data?.user?.name || {};
  return {
    identity_token: authn.id_token,
    authorization_code: authn.code || null,
    first_name: nameUser.firstName || null,
    last_name: nameUser.lastName || null,
  };
}

export default function AppleSignInButton({ onSuccess, onError, testid }) {
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    if (busy) return;
    setBusy(true);
    try {
      const nonce = await getServerNonce();
      const native = await isNativeIOS();
      const authResult = native
        ? await runNativeAppleSignIn(nonce)
        : await runWebAppleSignIn(nonce);

      if (!authResult.identity_token) {
        throw new Error("Apple returned no identity token");
      }

      const login = await api.post("/auth/apple", {
        identity_token: authResult.identity_token,
        authorization_code: authResult.authorization_code,
        nonce,
        first_name: authResult.first_name,
        last_name: authResult.last_name,
      });
      if (onSuccess) onSuccess(login.data);
      else window.location.assign("/community");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Apple sign-in failed.";
      const canceled = String(msg).toLowerCase().includes("cancel")
        || String(err?.error || "").toLowerCase().includes("cancel");
      if (!canceled) {
        // eslint-disable-next-line no-console
        console.error("Apple sign-in error:", err);
        if (onError) onError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={testid || "apple-login-btn"}
      aria-label="Continue with Apple"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        width: "100%",
        minHeight: 44,
        padding: "12px 18px",
        borderRadius: 999,
        background: "#000000",
        color: "#FFFFFF",
        border: "1px solid #000000",
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: 0,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.75 : 1,
        transition: "opacity 140ms ease, transform 140ms ease",
      }}
    >
      <AppleLogo />
      <span>{busy ? "Signing in…" : "Continue with Apple"}</span>
    </button>
  );
}
